import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, LogOut, LayoutDashboard, Sparkles } from "lucide-react";

const navLinks = [
  { to: "/jobs", label: "Jobs" },
  { to: "/projects", label: "Projects" },
  { to: "/employers", label: "Employers" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="bg-[#FDFBF7]/85 backdrop-blur-md border-b border-[#EAE3DB] sticky top-0 z-50">
      <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto h-20 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2 group">
          <div className="w-9 h-9 rounded-xl bg-[#7A222B] flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-[#FDF0EC]" strokeWidth={2.2} />
          </div>
          <span className="font-display text-2xl font-800 tracking-tight text-[#2D2825]" style={{ fontWeight: 800 }}>
            Master
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm font-medium transition-colors hover:text-[#7A222B] ${
                location.pathname === l.to ? "text-[#7A222B]" : "text-[#6E665E]"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="text-sm font-medium text-[#6E665E] hover:text-[#7A222B] transition-colors">
                  Admin
                </Link>
              )}
              <Link
                to="/dashboard"
                data-testid="nav-dashboard"
                className="flex items-center gap-2 text-sm font-medium text-[#2D2825] hover:text-[#7A222B] transition-colors"
              >
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              <button
                onClick={onLogout}
                data-testid="nav-logout-btn"
                className="flex items-center gap-2 text-sm font-medium text-[#6E665E] hover:text-[#7A222B] transition-colors"
              >
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-signin" className="text-sm font-medium text-[#2D2825] hover:text-[#7A222B] transition-colors">
                Sign in
              </Link>
              <Link
                to="/register"
                data-testid="nav-get-started-btn"
                className="bg-[#7A222B] hover:bg-[#5C1D24] text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors shadow-sm"
              >
                Get started
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 text-[#2D2825]"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-btn"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#EAE3DB] bg-[#FDFBF7] px-6 py-4 flex flex-col gap-4">
          {navLinks.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-[#2D2825] font-medium" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
              {l.label}
            </Link>
          ))}
          <div className="h-px bg-[#EAE3DB]" />
          {user ? (
            <>
              <Link to="/dashboard" onClick={() => setOpen(false)} className="text-[#2D2825] font-medium">Dashboard</Link>
              {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="text-[#2D2825] font-medium">Admin</Link>}
              <button onClick={onLogout} className="text-left text-[#7A222B] font-medium">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="text-[#2D2825] font-medium">Sign in</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="bg-[#7A222B] text-white px-5 py-2.5 rounded-full text-sm font-medium text-center">Get started</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
